import { useConfirmDialog } from "contexts/confirmDialog";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useRef } from "react";
import {
  ReviewPeriodData,
  ReviewSlotRevision,
} from "src/store/api/generatedApi";
import { ScrapEyesEventWithReviewData } from "../contexts/ManualReviewEventContext";
import { hasConflictReasons } from "../utils/reviewReasons";
import { useReviewSaveError } from "./useReviewSaveError";

/**
 * Wraps an action so a repeated trigger — an auto-repeating hotkey, a double click —
 * can't start a second run while the first is still working through its own
 * confirmation prompts and modal chain.
 */
export const useRunExclusively = (): ((
  action: () => Promise<void>
) => () => Promise<void>) => {
  const inProgressRef = useRef(false);
  return useCallback(
    (action: () => Promise<void>) => async () => {
      if (inProgressRef.current) return;
      inProgressRef.current = true;
      try {
        await action();
      } finally {
        inProgressRef.current = false;
      }
    },
    []
  );
};

/**
 * Whether to proceed with a verify/submit save: unresolved conflicts or a paired
 * lift/drop with disagreeing materials, anywhere the save will mark verified. Reads
 * the save's own scope, so an off-window period brought in across a boundary is held
 * to the same guardrail as the page the reviewer is looking at. A verify can trip both
 * warnings at once; they're asked about in a single confirmation so the reviewer isn't
 * stopped by two dialogs in a row.
 */
export const useConfirmSaveWarnings = (): ((
  eventsInScope: ScrapEyesEventWithReviewData[]
) => Promise<boolean>) => {
  const { t } = useTenantTranslation();
  const confirm = useConfirmDialog();
  return useCallback(
    (eventsInScope: ScrapEyesEventWithReviewData[]): Promise<boolean> => {
      const hasUnresolvedConflicts = eventsInScope.some(
        (event) =>
          hasConflictReasons(event.review_reasons) && event.resolved !== true
      );
      const hasPairMismatch = eventsInScope.some((event) =>
        event.warnings?.includes("materialMismatchWithPair")
      );
      if (hasUnresolvedConflicts && hasPairMismatch) {
        return confirm({
          title: t("confirm"),
          message: t("conflictsAndMismatchWarning"),
        });
      }
      if (hasUnresolvedConflicts) {
        return confirm({
          title: t("confirm"),
          message: t("unverifiedConflictsWarning"),
        });
      }
      if (hasPairMismatch) {
        return confirm({
          title: t("confirm"),
          message: t("materialMismatchWarning"),
        });
      }
      return Promise.resolve(true);
    },
    [confirm, t]
  );
};

export type VerifyPeriodsFn = (
  periods: ReviewPeriodData[],
  eventsInScope: ScrapEyesEventWithReviewData[],
  originalEventsInScope: ScrapEyesEventWithReviewData[],
  options?: {
    includeVerified?: boolean;
    verifyPeriodStarts?: string[];
    reviewSlotRevisions?: ReviewSlotRevision[];
  }
) => Promise<void>;

/**
 * Writes a verify save through `verifyPeriods`, catching a failure behind the same
 * error dialog every save path uses. Resolves to whether the write succeeded.
 */
export const usePersistVerifiedPeriods = (
  verifyPeriods: VerifyPeriodsFn
): ((
  periods: ReviewPeriodData[],
  eventsInScope: ScrapEyesEventWithReviewData[],
  originalEventsInScope: ScrapEyesEventWithReviewData[],
  verifyPeriodStarts?: string[],
  reviewSlotRevisions?: ReviewSlotRevision[]
) => Promise<boolean>) => {
  const reportReviewSaveError = useReviewSaveError();
  return useCallback(
    async (
      periods: ReviewPeriodData[],
      eventsInScope: ScrapEyesEventWithReviewData[],
      originalEventsInScope: ScrapEyesEventWithReviewData[],
      verifyPeriodStarts?: string[],
      reviewSlotRevisions?: ReviewSlotRevision[]
    ): Promise<boolean> => {
      try {
        await verifyPeriods(periods, eventsInScope, originalEventsInScope, {
          includeVerified: true,
          verifyPeriodStarts,
          reviewSlotRevisions,
        });
        return true;
      } catch (error) {
        await reportReviewSaveError(error);
        return false;
      }
    },
    [verifyPeriods, reportReviewSaveError]
  );
};

/**
 * Tells the reviewer a bring-in chain hit its hop cap and stopped short, through the
 * same error dialog every save failure uses — the reviewer must not treat what the
 * chain resolved as complete, and nothing was saved.
 */
export const useReportChainCapped = (): (() => Promise<void>) => {
  const { t } = useTenantTranslation();
  const confirm = useConfirmDialog();
  return useCallback(async (): Promise<void> => {
    await confirm({
      title: t("globalErrorTitle"),
      message: t("bringInChainTooLongError"),
    });
  }, [confirm, t]);
};
